/**
 * Loads externalized parameters per artifact for all forward stages in one
 * multi-endpoint call (POST /api/transport/artifact-configs-multi).
 *
 * Cache key = getArtifactCacheKey(sourceId, targetInstanceId, werkKennung),
 * based on the base stage (the first forward stage) so plant isolation keeps
 * working as before. The response is lifted into the context as SET_CFG
 * (entries + stageValues).
 */

import { useCallback } from "react";
import { api } from "../../../utils/helpers";
import {
  getArtifactCacheKey,
  normalizeConfigEntry,
} from "../../../utils/transport-config-helpers";
import { useTransportV2, vorwaertsStufen } from "../context/TransportV2Context";

// Per-stage suffix: sourceId + kennung suffix, following the existing
// convention of appending the plant suffix to the artifact id (Flow_97,
// Flow_31, ...).
function suffixedIdFor(baseId, werkKennung) {
  if (!werkKennung) return baseId;
  return `${baseId}${werkKennung}`;
}

export function useArtifactConfigs() {
  const { state, dispatch } = useTransportV2();

  const loadFor = useCallback(async ({ sourceInstanceId, targetInstanceId, sourceArtifactId, suffixedId, werkKey, werkKennung }) => {
    const cacheKey = getArtifactCacheKey(sourceArtifactId, targetInstanceId, werkKennung);
    // Already loaded: nothing to do.
    const existing = state.cfg?.[werkKey]?.[sourceArtifactId];
    if (existing && existing.cacheKey === cacheKey) return existing.entries;

    // All forward stages of the space become targets. The base is the first
    // stage (typically QAS or whatever target stage the space is configured
    // for). Without forward stages there is nothing to derive, so the old
    // legacy case (only targetInstanceId as target) is served as a fallback.
    const stufen = vorwaertsStufen(state);
    const targets = [];
    if (stufen.length > 0) {
      for (const st of stufen) {
        // For the base stage we use suffixedId from the precheck (already
        // correct for the kennung); for later stages we rebuild the same
        // suffix, since the target tenant flow is named "Flow<kennung>"
        // there too. A single-stage space ends up with just the one entry.
        const targetFlowId = st.key === stufen[0].key
          ? (suffixedId || suffixedIdFor(sourceArtifactId, werkKennung))
          : suffixedIdFor(sourceArtifactId, werkKennung);
        targets.push({ stage: st.key, targetInstanceId: st.instanceId, targetFlowId });
      }
    } else {
      // Legacy fallback: no forward stages can be derived (for example a test without Scene0 state).
      targets.push({ stage: "qas", targetInstanceId, targetFlowId: suffixedId || sourceArtifactId });
    }

    try {
      const r = await api("/api/transport/artifact-configs-multi", {
        method: "POST",
        timeout: 60000,
        body: JSON.stringify({
          sourceInstanceId,
          sourceArtifactId,
          targets,
        }),
      });
      const base = Array.isArray(r?.base) ? r.base : [];
      const byStage = (r && r.byStage && typeof r.byStage === "object") ? r.byStage : {};
      // Base stage: its current values feed normalizeConfigEntry(targetValue)
      // and become the prefill for the root entry (newValue). Later stages
      // go into byStage.
      const basisStageKey = targets[0]?.stage || null;
      const basisValuesArr = basisStageKey ? (byStage[basisStageKey]?.values || []) : [];
      const basisValueLookup = {};
      for (const v of basisValuesArr) {
        if (v && v.ParameterKey != null) basisValueLookup[v.ParameterKey] = v.targetValue;
      }
      const entries = base
        .map((cfg) => {
          const targetValue = basisValueLookup[cfg.ParameterKey];
          // normalizeConfigEntry expects targetValue as a property of the raw entry.
          return normalizeConfigEntry({ ...cfg, targetValue: targetValue == null ? undefined : targetValue });
        })
        .filter(Boolean);
      // stageValues holds all later stages (not the base); the reducer enriches byStage.
      const stageValues = {};
      for (const [stageKey, payload] of Object.entries(byStage)) {
        if (stageKey === basisStageKey) continue;
        stageValues[stageKey] = payload?.values || [];
        // Fault tolerant: byStage[stage].error is not treated as a blocker,
        // the stage simply shows no current value. Nothing throws here so a
        // 403/404 on one stage does not break the whole configuration view.
      }
      dispatch({ type: "SET_CFG", payload: { werkKey, sourceId: sourceArtifactId, cacheKey, entries, stageValues } });
      return entries;
    } catch (e) {
      dispatch({ type: "SET_CFG", payload: { werkKey, sourceId: sourceArtifactId, cacheKey, entries: [] } });
      throw e;
    }
    // Deliberately narrow deps: state.cfg (cache check) and state.space
    // (stage derivation). A broader [state] dependency would recreate
    // loadFor on every reducer change and trigger unnecessary reloads in
    // Scene4.
  }, [state.cfg, state.space, dispatch]);

  return { loadFor };
}
